import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useMemo } from 'react';
import { Box, Typography, Card } from '@mui/material';

const containerStyle = {
  width: '100%',
  height: '400px'
};

export function RestaurantMap({ locations = [] }) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  // Calculate center and zoom based on locations
  const { center, zoom, hasValidLocations } = useMemo(() => {
    const validLocations = locations.filter(
      loc => loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number'
    );

    if (validLocations.length === 0) {
      return { center: null, zoom: null, hasValidLocations: false };
    }

    // Single location - center on it with closer zoom
    if (validLocations.length === 1) {
      return {
        center: {
          lat: validLocations[0].latitude,
          lng: validLocations[0].longitude
        },
        zoom: 15,
        hasValidLocations: true
      };
    }

    // Multiple locations - calculate bounds center
    const bounds = validLocations.reduce(
      (acc, loc) => ({
        minLat: Math.min(acc.minLat, loc.latitude),
        maxLat: Math.max(acc.maxLat, loc.latitude),
        minLng: Math.min(acc.minLng, loc.longitude),
        maxLng: Math.max(acc.maxLng, loc.longitude),
      }),
      {
        minLat: validLocations[0].latitude,
        maxLat: validLocations[0].latitude,
        minLng: validLocations[0].longitude,
        maxLng: validLocations[0].longitude,
      }
    );

    return {
      center: {
        lat: (bounds.minLat + bounds.maxLat) / 2,
        lng: (bounds.minLng + bounds.maxLng) / 2,
      },
      zoom: 13,
      hasValidLocations: true
    };
  }, [locations]);

  if (!isLoaded) return <div>Loading map…</div>;

  // No valid locations - show message instead of map
  if (!hasValidLocations) {
    return (
      <Card sx={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Location Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Location information will be displayed here once added.
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <GoogleMap 
      mapContainerStyle={containerStyle} 
      center={center} 
      zoom={zoom}
    >
      {locations.map((loc, i) => {
        if (!loc || typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') {
          return null;
        }
        
        return (
          <Marker 
            key={loc.id || i} 
            position={{ lat: loc.latitude, lng: loc.longitude }}
            title={loc.street || loc.address || 'Restaurant location'}
          />
        );
      })}
    </GoogleMap>
  );
}